import type { AIConversation, AIMessage } from '../types/contracts';
import { shortTime } from '../lib/format';
import { TakeoverControl } from './TakeoverControl';

function Bubble({ m }: { m: AIMessage }) {
  if (m.author === 'system') {
    return (
      <div className="flex justify-center">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 text-[11px] font-semibold text-gray-500">
          <i className="ri-information-line" /> {m.body}
        </span>
      </div>
    );
  }
  const isCustomer = m.author === 'customer';
  const isAi = m.author === 'ai_agent';
  return (
    <div className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
        isCustomer
          ? 'bg-gray-100 text-gray-800'
          : isAi
            ? 'bg-teal-600 text-white'
            : 'bg-indigo-600 text-white'
      }`}>
        <p className={`text-[10px] font-black tracking-wide mb-0.5 ${isCustomer ? 'text-gray-400' : 'text-white/70'}`}>
          {isAi ? '🤖 ' : ''}{m.author_name}
        </p>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.body}</p>
        <p className={`text-[10px] mt-1 flex items-center gap-1 ${isCustomer ? 'text-gray-400' : 'text-white/60'}`}>
          {shortTime(m.sent_at)}
          {m.delivery && (
            <span className="inline-flex items-center">
              {m.delivery === 'read' ? <i className="ri-check-double-line text-sky-300" />
                : m.delivery === 'delivered' ? <i className="ri-check-double-line" />
                : m.delivery === 'failed' ? <i className="ri-error-warning-line text-rose-300" />
                : <i className="ri-check-line" />}
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

/** Safe business decision summary — no chain-of-thought. */
function AiDecisionCard({ conversation }: { conversation: AIConversation }) {
  const d = conversation.decision;
  if (!d) return null;
  return (
    <div className="rounded-2xl border border-teal-100 bg-teal-50/60 p-4">
      <h3 className="text-[10px] font-black tracking-widest text-teal-700 mb-2">قرار المساعد الذكي</h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div><dt className="text-gray-500">النية</dt><dd className="font-bold text-gray-800">{d.intent}</dd></div>
        <div><dt className="text-gray-500">حرارة العميل</dt><dd className="font-bold text-gray-800 capitalize">{d.lead_temperature}</dd></div>
        <div><dt className="text-gray-500">المرحلة</dt><dd className="font-bold text-gray-800 capitalize">{d.stage}</dd></div>
        <div><dt className="text-gray-500">الثقة</dt><dd className="font-bold text-teal-700">{d.confidence}%</dd></div>
        <div className="col-span-2"><dt className="text-gray-500">الإجراء الموصى به</dt><dd className="font-bold text-gray-900">{d.recommended_action}</dd></div>
        <div className="col-span-2"><dt className="text-gray-500">السبب</dt><dd className="text-gray-700">{d.reason}</dd></div>
      </dl>
    </div>
  );
}

export function ConversationView({ conversation }: { conversation: AIConversation }) {
  return (
    <div className="rounded-2xl bg-white border border-gray-100 flex flex-col min-h-[60vh]">
      <header className="px-5 py-3.5 border-b border-gray-50 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-gray-900">{conversation.customer_name} — {conversation.business_name}</p>
          <p className="text-[11px] text-gray-400 capitalize">{conversation.channel} · {conversation.online ? 'متصل' : 'غير متصل'}</p>
        </div>
        <TakeoverControl conversationId={conversation.id} initialHandler={conversation.handler} />
      </header>
      <div className="flex-1 px-5 py-4 space-y-3 overflow-y-auto">
        {(conversation.messages ?? []).map((m) => <Bubble key={m.id} m={m} />)}
      </div>
      <footer className="px-5 py-3 border-t border-gray-50">
        <AiDecisionCard conversation={conversation} />
      </footer>
    </div>
  );
}
